/**
 * My request component.
 *
 * Displays and manages user's partner requests. Provides functionality to
 * view, edit, pause/resume, and delete requests. Handles both list view and
 * edit form view based on route state.
 *
 * @module MyRequest
 */

import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { usePartnerFinderPath } from "@/hooks/usePartnerFinderPath";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Globe,
  MapPin,
  Clock,
  Edit,
  Pause,
  PlayCircle,
  Trash2,
} from "lucide-react";
import { PartnerRequest, RequestStatus } from "@/types/partner-finder";
import RequestForm from "./RequestForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";
import { usePartnerRequests } from "@/hooks/usePartnerRequests";
import { usePartnerRequestForEdit } from "@/hooks/usePartnerRequestForEdit";
import { ROUTES } from "@/lib/routeConstants";
import { useTranslation } from "react-i18next";
import {
  translateMeetingPreference,
  translatePartnerDays,
  translatePartnerGoal,
  translatePartnerLocation,
  translateRequestStatus,
} from "@/lib/partnerFinderLabels";

const getStatusBadgeVariant = (status: RequestStatus) => {
  switch (status) {
    case "active":
      return "default";
    case "paused":
      return "secondary";
    case "expired":
      return "outline";
    default:
      return "secondary";
  }
};

function MyRequestContent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const paths = usePartnerFinderPath();
  const { user } = useAuth();
  const {
    requests: userRequests,
    isLoading,
    isPausing,
    requestToDelete,
    setRequestToDelete,
    handlePauseToggle,
    handleDelete: handleDeleteRequest,
  } = usePartnerRequests(user?.id);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleEdit = (requestId: string) => {
    navigate(paths.requestEdit, { state: { requestId } });
  };

  const handleDeleteClick = (requestId: string) => {
    setRequestToDelete(requestId);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!requestToDelete) return;
    await handleDeleteRequest(requestToDelete);
    setDeleteDialogOpen(false);
  };

  if (isLoading) {
    return (
      <Card className="p-8 lg:p-12 text-center">
        <p className="text-muted-foreground">
          {t("partnerFinder.requests.loadingRequest", {
            defaultValue: "Loading your request...",
          })}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6 lg:space-y-8">
      {userRequests.length > 0 ? (
        <>
          {/* Section Header */}
          <div>
            <h2 className="text-2xl font-semibold">
              {t("partnerFinder.requests.title", {
                defaultValue: "My Partner Requests",
              })}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("partnerFinder.requests.count", {
                count: userRequests.length,
                defaultValue: `You have ${userRequests.length} ${userRequests.length === 1 ? "request" : "requests"}`,
              })}
            </p>
          </div>

          {/* Request Cards */}
          <div className="space-y-4">
            {userRequests.map((request) => {
              const availabilityText = translatePartnerDays(
                t,
                request.availability,
              );
              const meetingPreferenceLabel = translateMeetingPreference(
                t,
                request.meetingPreference,
              );
              return (
                <Card key={request.id}>
                  <CardHeader className="p-4 sm:p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <CardTitle>
                            {t("partnerFinder.requests.cardTitle", {
                              defaultValue: "Partner Request",
                            })}
                          </CardTitle>
                          <Badge
                            variant={getStatusBadgeVariant(request.status)}
                          >
                            {translateRequestStatus(t, request.status)}
                          </Badge>
                        </div>
                        <CardDescription>
                          {t("partnerFinder.requests.lastUpdated", {
                            date: new Date(
                              request.updatedAt,
                            ).toLocaleDateString(i18n.language),
                            defaultValue: `Last updated ${new Date(request.updatedAt).toLocaleDateString()}`,
                          })}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 p-4 pt-0 sm:p-6 sm:pt-0">
                    {/* Request Details */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
                      <div className="space-y-1">
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          {t("partnerFinder.search.germanLevelLabel", {
                            defaultValue: "German Level",
                          })}
                        </p>
                        <Badge
                          variant="outline"
                          className="text-sm font-semibold px-3 py-1"
                        >
                          {request.level}
                        </Badge>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          {t("partnerFinder.search.learningGoalLabel", {
                            defaultValue: "Learning Goal",
                          })}
                        </p>
                        <Badge
                          variant="secondary"
                          className="text-sm px-3 py-1"
                        >
                          {translatePartnerGoal(t, request.goal)}
                        </Badge>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          {t("partnerFinder.search.meetingPreferenceLabel", {
                            defaultValue: "Meeting Preference",
                          })}
                        </p>
                        <div className="flex items-center gap-2 text-sm">
                          {request.meetingPreference === "online" ? (
                            <div className="flex items-center gap-2 px-3 py-1 bg-primary/5 rounded-md text-primary font-medium border border-primary/10">
                              <Globe className="h-4 w-4" />
                              <span>{meetingPreferenceLabel}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 px-3 py-1 bg-primary/5 rounded-md text-primary font-medium border border-primary/10">
                              <MapPin className="h-4 w-4" />
                              <span>{meetingPreferenceLabel}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {request.availability.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                            {t("partnerFinder.search.availabilityLabel", {
                              defaultValue: "Availability",
                            })}
                          </p>
                          <div className="flex items-start gap-2 text-sm text-foreground/80">
                            <Clock className="h-4 w-4 mt-0.5 shrink-0 text-primary/60" />
                            <span className="font-medium leading-relaxed">
                              {availabilityText}
                            </span>
                          </div>
                        </div>
                      )}
                      {request.location && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                            {t("partnerFinder.search.locationLabel", {
                              defaultValue: "Location",
                            })}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-foreground/80">
                            <MapPin className="h-4 w-4 text-primary/60" />
                            <span className="font-medium">
                              {translatePartnerLocation(t, request.location)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {request.additionalInfo && (
                      <div className="bg-muted/30 p-4 rounded-lg border border-border/50">
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                          {t("partnerFinder.form.additionalInfoLabel", {
                            defaultValue: "Additional Information",
                          })}
                        </p>
                        <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">
                          {request.additionalInfo}
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                      <Button
                        onClick={() => handleEdit(request.id)}
                        className="w-full sm:flex-1 h-11 sm:h-9"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        {t("partnerFinder.buttons.editRequest", {
                          defaultValue: "Edit Request",
                        })}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => handlePauseToggle(request.id)}
                        disabled={isPausing === request.id}
                        className="w-full sm:flex-1 h-11 sm:h-9 border border-input"
                      >
                        {request.status === "active" ? (
                          <>
                            <Pause className="h-4 w-4 mr-2" />
                            {t("partnerFinder.buttons.pause", {
                              defaultValue: "Pause",
                            })}
                          </>
                        ) : (
                          <>
                            <PlayCircle className="h-4 w-4 mr-2" />
                            {t("partnerFinder.buttons.resume", {
                              defaultValue: "Resume",
                            })}
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => handleDeleteClick(request.id)}
                        className="w-full sm:w-auto h-11 sm:h-9 text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t("partnerFinder.buttons.delete", {
                          defaultValue: "Delete",
                        })}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      ) : (
        /* No Request - Onboarding Card */
        <Card className="p-8 lg:p-12 text-center">
          <div className="space-y-4 max-w-md mx-auto">
            <h2 className="text-2xl font-semibold">
              {t("partnerFinder.requests.createTitle", {
                defaultValue: "Create your partner request",
              })}
            </h2>
            <p className="text-muted-foreground">
              {t("partnerFinder.requests.createDescription", {
                defaultValue:
                  "Share your learning goals and preferences to help other learners find you. Your request will be visible to potential partners.",
              })}
            </p>
            <Button
              onClick={() => navigate(paths.requestEdit)}
              className="mt-4"
            >
              {t("partnerFinder.buttons.createRequest", {
                defaultValue: "Create Request",
              })}
            </Button>
          </div>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("partnerFinder.dialogs.deleteRequest.title", {
                defaultValue: "Delete Request",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("partnerFinder.dialogs.deleteRequest.description", {
                defaultValue:
                  "Are you sure you want to delete this partner request? This action cannot be undone.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRequestToDelete(null)}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("partnerFinder.buttons.delete", { defaultValue: "Delete" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function MyRequest() {
  const location = useLocation();
  const isEditMode = location.pathname.includes(ROUTES.PARTNERS_REQUEST_EDIT);

  if (isEditMode) {
    return <RequestFormWrapper />;
  }

  return <MyRequestContent />;
}

function RequestFormWrapper() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const paths = usePartnerFinderPath();
  const { user } = useAuth();
  const { request, isLoading, isCreatingNew } = usePartnerRequestForEdit(
    user?.id,
  );

  const handleFormSubmit = async (
    _requestData: Omit<
      PartnerRequest,
      "id" | "userId" | "createdAt" | "updatedAt" | "status"
    >,
  ) => {
    // This will be handled by RequestForm component
    navigate(paths.request);
  };

  if (isLoading) {
    return (
      <Card className="p-8 lg:p-12 text-center">
        <p className="text-muted-foreground">
          {t("common.loading", { defaultValue: "Loading..." })}
        </p>
      </Card>
    );
  }

  // If creating new, don't pass initialData. If editing, pass the existing request.
  return (
    <RequestForm
      onSubmit={handleFormSubmit}
      initialData={isCreatingNew ? undefined : request || undefined}
    />
  );
}
